import CoreBase from '../../../services/engines/CoreBase';
import { PV_DRAW_MIN_W, PV_ROW_KIND, PV_SECTION_FALLBACK } from './plugVueConst';

const byAlias = (a, b) => a.alias.localeCompare(b.alias);
// offline plugs keep their last reading — never count it
const watts = (r) => (r.online ? (r.power ?? 0) : 0);

// Pure grouping logic — sections, children indented under their root (cross-section as reference), untracked row per root
class PlugVueSectionBuilder extends CoreBase {
    _name = 'PlugVueSectionBuilder';

    /**
     * Builds the section layout from the flat plug rows.
     * @param {object[]} rows - plug rows from the cache
     * @param {boolean} drawing - hide plugs at or below PV_DRAW_MIN_W
     * @param {string[]} sectionNames - BE section order, fallback section is appended
     * @param {string|null} only - single section name, null = all sections
     * @returns {{section: string, totalW: number, rows: object[]}[]} non-empty sections
     */
    build = (rows, drawing, sectionNames, only = null) => {
        try {
            const layout = [...new Set([...sectionNames, PV_SECTION_FALLBACK])];
            // unknown/empty section from BE → fallback
            const resolved = rows.map((r) => (layout.includes(r.section) ? r : { ...r, section: PV_SECTION_FALLBACK }));
            const ctx = this._context(resolved, drawing);
            const wanted = only ? layout.filter((s) => s === only) : layout;
            return wanted.map((section) => this._buildSection(ctx, section)).filter((s) => s.rows.length);
        } catch (error) {
            this.logError('build', error.message);
            return [];
        }
    };

    /**
     * @param {object[]} rows
     * @param {boolean} drawing
     * @returns {object[]} pinned plug rows, alias order
     */
    pinned = (rows, drawing) => {
        const ctx = this._context(rows, drawing);
        return rows.filter((r) => r.pinned && ctx.visible(r)).sort(byAlias).map((r) => ({ ...r, kind: PV_ROW_KIND.plug, depth: 0 }));
    };

    /**
     * @param {object[]} rows
     * @returns {number} total W of real roots and loose plugs (children are inside their root)
     */
    totalW = (rows) => rows.filter((r) => !r.parentRk).reduce((sum, r) => sum + watts(r), 0);

    _context = (rows, drawing) => {
        const byRk = new Map(rows.map((r) => [r.rk, r]));
        const kidsOf = (rk) => rows.filter((r) => r.parentRk === rk);
        const draws = (r) => watts(r) > PV_DRAW_MIN_W;
        const visible = (r) => !drawing || draws(r) || kidsOf(r.rk).some(draws);
        return { rows, byRk, kidsOf, visible };
    };

    _buildSection = (ctx, section) => {
        const list = ctx.rows.filter((r) => r.section === section).sort(byAlias);
        // top = no parent, or parent lives in another section (then shown flat here)
        const isTop = (r) => !r.parentRk || ctx.byRk.get(r.parentRk)?.section !== section;
        const out = [];
        let totalW = 0;

        list.filter(isTop).forEach((root) => {
            totalW += watts(root);
            if (!ctx.visible(root)) return;

            out.push({ ...root, kind: PV_ROW_KIND.plug, depth: 0 });

            // all children under their root, also those living in another section (not counted here)
            const kids = ctx.kidsOf(root.rk);
            kids.filter(ctx.visible)
                .sort(byAlias)
                .forEach((k) => out.push({ ...k, kind: PV_ROW_KIND.plug, depth: 1 }));

            if (kids.length) out.push(this._untracked(root, kids));
        });

        return { section, totalW, rows: out };
    };

    _untracked = (root, kids) => ({
        rk: `${root.rk}::untracked`,
        kind: PV_ROW_KIND.untracked,
        depth: 1,
        alias: 'Untracked',
        name: '',
        power: root.online ? watts(root) - kids.reduce((sum, k) => sum + watts(k), 0) : null,
        amp: null,
        status: null,
    });
}

const plugVueSectionBuilder = new PlugVueSectionBuilder();
export default plugVueSectionBuilder;
